import * as React from 'react'
import { DownTime } from '../../model/DownTime'
import {
  ButtonGroup,
  Button,
  Col,
  FormControl,
  FormCheck,
  Row,
  Modal,
  Container
} from 'react-bootstrap'
import { ReadOnlyEventList, timeProps } from './ReadOnlyEventList'
import { ChangeEvent } from 'react'
import ReactTable, { Filter, RowInfo } from 'react-table'

interface DispatchProps {
  close: () => void
  copy: (source: DownTime, targets: DownTime[]) => void
  dateFormatter: (date?: Date) => JSX.Element
  getDowntimes: (from: Date, to: Date) => Promise<DownTime[]>
}

interface StateProps {
  downtime: DownTime
  scadaObjectType: string
}

type State = {
  objectNameFilter: string
  timeFromString: string
  timeToString: string
  withinTimeRange: boolean
  targetEvents?: SelectableDownTime[]
  selectedFilteredRowIds: string[]
  fetchError: string
  loading: boolean
  forceCheckAllRerenderIndex: number
}

type Props = DispatchProps & StateProps

class SelectableDownTime extends DownTime {
  public constructor(init: Partial<DownTime>, selected: boolean = false) {
    super(init)
    this.selected = selected
  }
  selected: boolean
}

export class CopyModal extends React.Component<Props, State> {
  dataTableElement?: ReactTable<SelectableDownTime>
  dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC'
  }

  constructor(props: Props) {
    super(props)

    const fifteenMinutes = 15 * 60 * 1000
    this.state = {
      objectNameFilter: '',
      timeFromString: new Date(props.downtime.timeOn.getTime() - fifteenMinutes).toLocaleDateString(
        'ja',
        this.dateOptions
      ),
      timeToString: new Date(props.downtime.timeOn.getTime() + fifteenMinutes).toLocaleDateString(
        'ja',
        this.dateOptions
      ),
      withinTimeRange: true,
      targetEvents: undefined,
      selectedFilteredRowIds: [],
      fetchError: '',
      loading: false,
      forceCheckAllRerenderIndex: 1
    }
  }

  overrideFilter = (visible: boolean, filter: Filter, row: any): boolean => {
    if (row.selected) {
      if (!visible) {
        if (!this.state.selectedFilteredRowIds.includes(row._original.id)) {
          this.setState({
            selectedFilteredRowIds: [row._original.id, ...this.state.selectedFilteredRowIds]
          })
        }
      }
      return true
    } else {
      if (this.state.selectedFilteredRowIds.includes(row._original.id)) {
        this.setState({
          selectedFilteredRowIds: this.state.selectedFilteredRowIds.filter(
            id => id !== row._original.id
          )
        })
      }
    }
    return visible
  }

  handleObjectNameFilterChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({ objectNameFilter: event.currentTarget.value || '' })
  }

  handleTimeFromChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({ timeFromString: event.currentTarget.value || '' })
  }

  handleTimeToChange = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState({ timeToString: event.currentTarget.value || '' })
  }

  handleWithinTimeRangeChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ withinTimeRange: event.currentTarget.checked })
  }

  handleSelectedChanged = (event: ChangeEvent<HTMLInputElement>, downtime: SelectableDownTime) => {
    this.setState({
      targetEvents:
        this.state.targetEvents &&
        this.state.targetEvents
          .filter(targetDowntime => targetDowntime.id !== downtime.id)
          .concat(new SelectableDownTime(downtime, event.currentTarget.checked))
    })
  }

  getVisibleDowntimeIds = (): string[] | undefined =>
    (this.dataTableElement as any)?.getResolvedState()?.sortedData?.map((i: any) => i._original.id)

  allVisibleDowntimesSelected = (): boolean => {
    const visibleDowntimeIds = this.getVisibleDowntimeIds()
    if (!this.state.targetEvents || !visibleDowntimeIds || visibleDowntimeIds.length === 0)
      return false
    const visibleDowntimes = this.state.targetEvents.filter(d => visibleDowntimeIds.includes(d.id))
    return visibleDowntimes.every(d => d.selected)
  }

  handleCheckAllEventsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const filteredDowntimes = this.getVisibleDowntimeIds()
    const targetState = event.currentTarget.checked
    this.setState({
      targetEvents:
        this.state.targetEvents &&
        this.state.targetEvents.map(downtime =>
          filteredDowntimes?.includes(downtime.id) ?? true
            ? new SelectableDownTime(downtime, targetState)
            : downtime
        )
    })
  }

  filterEvents = (events: DownTime[], dateFrom: Date, source: DownTime): SelectableDownTime[] =>
    events
      .filter(
        event =>
          event.id !== source.id &&
          (this.state.withinTimeRange ? event.timeOn >= dateFrom : true) &&
          (this.state.objectNameFilter
            ? event.scadaObjectName
                .toLocaleLowerCase()
                .search(this.state.objectNameFilter.toLocaleLowerCase()) >= 0
            : true)
      )
      .map(event => new SelectableDownTime(event))

  findEvents = () => {
    var dateFrom = new Date(Date.parse(this.state.timeFromString + ' UTC'))
    var dateTo = new Date(Date.parse(this.state.timeToString + ' UTC'))
    this.setState({ targetEvents: [], loading: true, fetchError: '' }, () =>
      this.props
        .getDowntimes(dateFrom, dateTo)
        .then(result =>
          this.setState({
            targetEvents: this.filterEvents(result, dateFrom, this.props.downtime),
            loading: false
          })
        )
        .catch(error => this.setState({ fetchError: error, loading: false }))
    )
  }

  public render() {
    const { downtime, dateFormatter } = this.props
    return (
      <Modal
        show={downtime !== null}
        onHide={() => this.props.close()}
        centered
        dialogClassName="modal-90w"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Copy Downtime Event</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container className="no-max-width">
            <Row>
              <Col>
                <h6>Downtime Event to be Copied</h6>
                <Row>
                  <Col className="field-name" xs={4}>
                    Scada Object
                  </Col>
                  <Col xs={8}>{downtime.scadaObjectName}</Col>
                </Row>
                <Row>
                  <Col className="field-name" xs={4}>
                    Time On
                  </Col>
                  <Col xs={8}>{dateFormatter(downtime.timeOn)}</Col>
                </Row>
                <Row>
                  <Col className="field-name" xs={4}>
                    Time Off
                  </Col>
                  <Col xs={8}>{dateFormatter(downtime.timeOff)}</Col>
                </Row>
                <Row>
                  <Col className="field-name" xs={4}>
                    Alarm Code
                  </Col>
                  <Col xs={8}>{downtime.alarmCode}</Col>
                </Row>
                <Row>
                  <Col className="field-name" xs={4}>
                    Description
                  </Col>
                  <Col xs={8}>{downtime.description}</Col>
                </Row>
              </Col>
              <Col>
                <h6>Settings to be copied</h6>
                <Row>
                  <Col className="field-name" xs={4}>
                    Comment
                  </Col>
                  <Col xs={8}>{downtime.comment}</Col>
                </Row>
                <Row>
                  <Col className="field-name" xs={4}>
                    Group
                  </Col>
                  <Col xs={8}>{downtime.group}</Col>
                </Row>
                <Row>
                  <Col className="field-name" xs={4}>
                    Sub Group
                  </Col>
                  <Col xs={8}>{downtime.subGroup}</Col>
                </Row>
                <Row>
                  <Col className="field-name" xs={4}>
                    Penalising Status
                  </Col>
                  <Col xs={8}>{downtime.penalisingStatus}</Col>
                </Row>
              </Col>
            </Row>
          </Container>
          <section className="filters">
            <span>Object:</span>
            <FormControl
              placeholder="Part of object name"
              type="Text"
              value={this.state.objectNameFilter}
              onChange={this.handleObjectNameFilterChange}
            />
            <span>Time From:</span>
            <FormControl
              placeholder="yyyy/mm/dd hh:mm:ss"
              value={this.state.timeFromString}
              onChange={this.handleTimeFromChange}
              isInvalid={isNaN(Date.parse(this.state.timeFromString))}
              {...timeProps()}
            />
            <span>To:</span>
            <FormControl
              placeholder="yyyy/mm/dd hh:mm:ss"
              value={this.state.timeToString}
              onChange={this.handleTimeToChange}
              isInvalid={isNaN(Date.parse(this.state.timeToString))}
              {...timeProps()}
            />
            <FormCheck
              label="Only events that start in range"
              title="If unchecked show all events where any part of the event is in the range"
              type="checkbox"
              checked={this.state.withinTimeRange}
              onChange={this.handleWithinTimeRangeChange}
            />
            <Button
              type="submit"
              variant="primary"
              disabled={
                isNaN(Date.parse(this.state.timeFromString)) ||
                isNaN(Date.parse(this.state.timeToString))
              }
              onClick={this.findEvents}
            >
              Find Events
            </Button>
          </section>
          <ReadOnlyEventList
            data={this.state.targetEvents}
            showScadaObjectsColumn={true}
            dateFormatter={this.props.dateFormatter}
            onFilteredChange={() =>
              this.setState({
                forceCheckAllRerenderIndex: this.state.forceCheckAllRerenderIndex + 1,
                selectedFilteredRowIds: []
              })
            }
            loading={this.state.loading}
            noDataText={
              this.state.targetEvents === undefined
                ? 'Press Find Events to load some events'
                : 'No target events found for the selected filters'
            }
            overrideFilter={(visible: boolean, filter: Filter, row: any): boolean =>
              this.overrideFilter(visible, filter, row)
            }
            defaultFilterMethod={(filter: Filter, row: any): boolean =>
              this.overrideFilter(
                row._original.doNotFilter ||
                  row._original.isNew ||
                  String(row[filter.id])
                    .toLocaleLowerCase()
                    .indexOf(filter.value.toLocaleLowerCase()) >= 0,
                filter,
                row
              )
            }
            ref={element => {
              if (element) {
                // from https://github.com/tannerlinsley/react-table/issues/495#issuecomment-331946312
                this.dataTableElement = element.dataTableElement
              }
            }}
            filterable
            getRowClasses={(rowInfo?: RowInfo) => {
              return this.state.selectedFilteredRowIds.includes(rowInfo?.original?.id ?? '')
                ? 'text-muted'
                : ''
            }}
            extraColumns={[
              {
                accessor: 'selected',
                filterable: false,
                Header: 'Copy To',
                minWidth: 60,
                maxWidth: 60,
                Cell: row => (
                  <FormCheck
                    type="checkbox"
                    checked={row.value}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      this.handleSelectedChanged(event, row.original)
                    }
                  />
                )
              }
            ]}
          />
        </Modal.Body>
        <Modal.Footer>
          <Container className="no-max-width">
            <Row>
              <Col xs={3}>
                <FormCheck
                  className={'fixed ' + this.state.forceCheckAllRerenderIndex}
                  label={
                    this.allVisibleDowntimesSelected()
                      ? 'Deselect All Visible Downtimes'
                      : 'Select All Visible Downtimes'
                  }
                  type="checkbox"
                  title={
                    this.allVisibleDowntimesSelected()
                      ? 'Unchecking this box will deselect all Downtime Events that are currently visible i.e. not hidden by filters'
                      : 'Checking this box will select all Downtime Events that are currently visible i.e. not hidden by filters'
                  }
                  disabled={!this.state.targetEvents}
                  checked={this.allVisibleDowntimesSelected()}
                  onChange={this.handleCheckAllEventsChange}
                />
              </Col>
              <Col xs={6}>
                {this.state.selectedFilteredRowIds.length > 0 && (
                  <span>
                    Info: Rows that are selected are never hidden by the filteres, but if they would
                    be filtered out they are shown with{' '}
                    <span className="text-muted">muted text</span>.
                  </span>
                )}
              </Col>
              <Col xs={3}>
                <ButtonGroup className="float-right">
                  <Button
                    type="submit"
                    disabled={
                      !(
                        this.state.targetEvents &&
                        this.state.targetEvents.some(e => e.selected && !e.saveInFlight)
                      )
                    }
                    variant="primary"
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                      this.props.copy(
                        downtime,
                        this.state.targetEvents
                          ? this.state.targetEvents.filter(event => event.selected)
                          : []
                      )
                      e.preventDefault()
                    }}
                  >
                    Copy
                  </Button>
                  <Button variant="warning" onClick={() => this.props.close()}>
                    Cancel
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>
          </Container>
        </Modal.Footer>
      </Modal>
    )
  }
}
